import { z } from "zod";
import { MODES, OPS, type Mode, type Op } from "../content/modes";
import { STARTER_NINJA } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import { updateSets } from "../game/sets";
import { coerceSaveV1 } from "./schemaV1";
import { migrateV1, migrateV2 } from "./migrate";

/**
 * The storage key predates save versions (and the Nine Scrolls name); the version lives inside
 * the save. Never rename it: existing saves on the device live under it.
 */
export const SAVE_KEY = "ninja-maths-v1";
export const SAVE_VERSION = 5;

/** Practice length in minutes; 0 means untimed (the child taps Done). */
export const PRACTICE_MINUTE_OPTIONS = [0, 1, 2, 3, 5] as const;
export const ADD_MAX_OPTIONS = [10, 20, 50, 100] as const;
export const TABLE_MAX_OPTIONS = [2, 5, 10, 12] as const;

export const HISTORY_LIMIT = 100;
export const MISSED_LIMIT = 200;

const int = z.number().int().nonnegative();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** A record whose broken entries are dropped rather than failing the whole record. */
function lenientRecord<T extends z.ZodType>(value: T) {
  return z
    .record(z.string(), z.unknown())
    .catch({})
    .transform((rec) => {
      const out: Record<string, z.output<T>> = {};
      for (const [k, v] of Object.entries(rec)) {
        const r = value.safeParse(v);
        if (r.success) out[k] = r.data;
      }
      return out;
    });
}

const DEFAULT_OPS: Record<Op, boolean> = { add: true, sub: true, mul: false, div: false };

const OpsSchema = z.object({
  add: z.boolean().catch(true),
  sub: z.boolean().catch(true),
  mul: z.boolean().catch(false),
  div: z.boolean().catch(false),
});

const PracticeSchema = z.object({
  minutes: z
    .number()
    .refine((m) => (PRACTICE_MINUTE_OPTIONS as readonly number[]).includes(m))
    .catch(0),
  addMax: z.number().int().positive().catch(20),
  tableMax: z.number().int().positive().catch(10),
});

const LoadoutSchema = z.object({
  active: z.array(z.string().nullable()).catch([]),
  passive: z.array(z.string().nullable()).catch([]),
});

export const SettingsSchema = z.object({
  ops: OpsSchema.catch(DEFAULT_OPS),
  /** Read questions aloud. */
  audio: z.boolean().catch(false),
  /** Show the progress bar in trials. Off for a child it stresses; display only. */
  trialBar: z.boolean().catch(true),
  practice: PracticeSchema.catch({ minutes: 0, addMax: 20, tableMax: 10 }),
  /** look[rankIndex]: which strand's variant the card shows on Home. */
  look: lenientRecord(z.enum(MODES)),
  /** The ninja the child fights with (a ninja id). Must be recruited; falls back if not. */
  fighter: z.string().catch(STARTER_NINJA),
  /** loadout[ninjaId]: the items fitted to each slot. A wish list; fitFor decides what's carried. */
  loadout: lenientRecord(LoadoutSchema),
});
export type Settings = z.output<typeof SettingsSchema>;
export type PracticeSettings = Settings["practice"];

const TrialRecordSchema = z.object({
  /** Best score on this trial. */
  best: int.catch(0),
  attempts: int.catch(0),
  /** ISO date the trial was first passed. */
  passed: isoDate.optional().catch(undefined),
  /** Seed of the most recent attempt, so it can be replayed. */
  lastSeed: int.optional().catch(undefined),
});
export type TrialRecord = z.output<typeof TrialRecordSchema>;

const TierStatSchema = z.object({ n: int, right: int, ms: int });
export type TierStat = z.output<typeof TierStatSchema>;

const HistoryEntrySchema = z.object({
  d: isoDate,
  mode: z.enum(MODES),
  kind: z.enum(["trial", "practice", "battle"]),
  /** The rank the trial was for. Missing on history carried over from Phase 1. */
  rankIndex: int.optional(),
  /** Battles: the ninjas involved and how it went. */
  enemy: z.string().optional(),
  fighter: z.string().optional(),
  won: z.boolean().optional(),
  /** Coins this round earned. Missing on rounds from before coins existed. */
  coins: int.optional(),
  score: int,
  tried: int,
  /** Time limit in seconds; 0 for untimed practice. */
  seconds: int,
  passed: z.boolean().optional(),
  /** Whether read-aloud was on. */
  audio: z.boolean().catch(false),
  seed: int.optional(),
  /** The strands a Mix round drew from. */
  ops: z.array(z.enum(OPS)).optional(),
  /** Battles: the kit fought with and the active items pressed. */
  items: z
    .array(z.object({ id: z.string(), level: int }))
    .optional()
    .catch(undefined),
  itemsUsed: z.array(z.string()).optional().catch(undefined),
  /** Which version of the trial numbers (content/trials.ts); missing means 1. */
  trialsVersion: int.optional(),
  wrong: int.optional(),
  /** Questions passed on with the Pass button (part of `wrong`). */
  passes: int.optional(),
  /** Per tier: questions answered, right, and total milliseconds taken. */
  tiers: z.record(z.string(), TierStatSchema).optional().catch(undefined),
});
export type HistoryEntry = z.output<typeof HistoryEntrySchema>;

const MasterySchema = z.object({
  seen: int,
  correct: int,
  /** Right answers in a row. */
  streak: int,
  last: isoDate,
});
export type Mastery = z.output<typeof MasterySchema>;

const MissedSchema = z.object({
  /** How many more times it was missed than got right since. */
  n: z.number().int().positive(),
  /** The question type it came from. */
  type: z.string(),
});
export type Missed = z.output<typeof MissedSchema>;

const OwnedItemSchema = z.object({
  level: z.number().int().positive(),
  /** ISO date first bought. */
  bought: isoDate,
});

const RosterEntrySchema = z.object({
  /** ISO date recruited; missing while the ninja has only been fought. */
  recruited: isoDate.optional().catch(undefined),
  fought: int.catch(0),
  won: int.catch(0),
});

const rankIndex = z
  .number()
  .int()
  .min(0)
  .max(RANKS.length - 1)
  .catch(0);

const ZERO_RANKS: Record<Mode, number> = { add: 0, sub: 0, mul: 0, div: 0, mix: 0 };

const SaveSchema = z.object({
  version: z.literal(SAVE_VERSION).catch(SAVE_VERSION),
  name: z.string().max(20).catch(""),
  mode: z.enum(MODES).catch("add"),
  settings: SettingsSchema.catch({
    ops: DEFAULT_OPS,
    audio: false,
    trialBar: true,
    practice: { minutes: 0, addMax: 20, tableMax: 10 },
    look: {},
    fighter: STARTER_NINJA,
    loadout: {},
  }),
  /** The rank held in each strand. Earned in trials, set by placement or a parent. */
  rank: z
    .object({ add: rankIndex, sub: rankIndex, mul: rankIndex, div: rankIndex, mix: rankIndex })
    .catch(ZERO_RANKS),
  /** trials[mode][rankIndex]: how the child has done on the trial that earns that rank. */
  trials: z.partialRecord(z.enum(MODES), lenientRecord(TrialRecordSchema)).catch({}),
  /** Most recent first, capped at HISTORY_LIMIT. */
  history: z
    .array(z.unknown())
    .catch([])
    .transform((rows) =>
      rows.flatMap((r) => {
        const p = HistoryEntrySchema.safeParse(r);
        return p.success ? [p.data] : [];
      }),
    ),
  /** Per question type. */
  mastery: lenientRecord(MasterySchema),
  /** Facts answered wrong, by fact key. */
  missed: lenientRecord(MissedSchema),
  /** Has seen the first-launch placement offer. */
  onboarded: z.boolean().catch(false),
  /** stripes[mode][rankIndex]: stripes (0–3) earned on the trial for that rank. */
  stripes: z.partialRecord(z.enum(MODES), lenientRecord(z.number().int().min(0).max(3))).catch({}),
  /** sets[rankIndex]: dates the card first turned gold and foil. */
  sets: lenientRecord(z.object({ gold: isoDate.optional(), foil: isoDate.optional() })),
  /** The overall ninja rank: the highest complete set, never lowered. */
  overall: rankIndex,
  /** Strands that have had the one-off v1.1 retune award, so it happens at most once. */
  retuned: z.array(z.enum(MODES)).catch([]),
  /** Coins earned, ever. Phase 4 adds spending. */
  coins: int.catch(0),
  /** roster[ninjaId]: recruited date and battle record. Seedling needs no entry. */
  roster: lenientRecord(RosterEntrySchema),
  /** Coins spent in the Dojo Shop, ever. Balance is coins − spent. */
  spent: int.catch(0),
  /** items[itemId]: the level owned and when it was first bought. */
  items: lenientRecord(OwnedItemSchema),
});

export type SaveState = z.output<typeof SaveSchema>;

/** The progress parts of a save, each falling back to empty if broken. */
export const ProgressSchema = SaveSchema.pick({
  rank: true,
  trials: true,
  history: true,
  mastery: true,
  missed: true,
  stripes: true,
  sets: true,
  overall: true,
  retuned: true,
  coins: true,
  roster: true,
  spent: true,
  items: true,
});

export { lenientRecord, DEFAULT_OPS };

export function defaultSave(): SaveState {
  return {
    version: SAVE_VERSION,
    name: "",
    mode: "add",
    settings: {
      ops: { ...DEFAULT_OPS },
      audio: false,
      trialBar: true,
      practice: { minutes: 0, addMax: 20, tableMax: 10 },
      look: {},
      fighter: STARTER_NINJA,
      loadout: {},
    },
    rank: { ...ZERO_RANKS },
    trials: {},
    history: [],
    mastery: {},
    missed: {},
    onboarded: false,
    stripes: {},
    sets: {},
    overall: 0,
    retuned: [],
    coins: 0,
    roster: {},
    spent: 0,
    items: {},
  };
}

/** Saved before version 2, by the prototype (no version) or Phase 1 (version 1). */
function isV1(raw: object): boolean {
  if ("version" in raw) return raw.version === 1;
  return "best" in raw || "unlocks" in raw || "played" in raw;
}

/** Today on this device, YYYY-MM-DD (not UTC). */
function localDate(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Saved by Phase 2a, before stripes, sets and the v1.1 trial numbers. */
function isV2(raw: object): boolean {
  return "version" in raw && raw.version === 2;
}

/**
 * Turn anything (a current save, a Phase 1 or prototype save, a partial
 * object, junk) into a valid current SaveState. Old saves are migrated;
 * unknown or broken fields fall back to defaults; good fields are kept.
 * Never throws.
 */
export function coerceSave(raw: unknown, today = localDate()): SaveState {
  const source = raw && typeof raw === "object" ? raw : {};
  const v1 = isV1(source);
  let parsed: SaveState = SaveSchema.parse(v1 ? migrateV1(coerceSaveV1(source)) : source);
  if (v1 || isV2(source)) parsed = migrateV2(parsed, today);
  const ops = { ...parsed.settings.ops };
  if (!OPS.some((k) => ops[k])) ops.add = true;
  // Sets follow the ranks held. Reconciling on every load only ever adds, so
  // an imported or hand-edited save can't show a rank without its gold card.
  const sets = updateSets(
    { rank: parsed.rank, ops, sets: parsed.sets, overall: parsed.overall },
    today,
  );
  return {
    ...parsed,
    settings: { ...parsed.settings, ops },
    history: parsed.history.slice(0, HISTORY_LIMIT),
    sets: sets.sets,
    overall: sets.overall,
  };
}

/** True if this looks like a Nine Scrolls save at all (used to reject random JSON on import). */
export function looksLikeSave(raw: unknown): boolean {
  return (
    !!raw &&
    typeof raw === "object" &&
    ["best", "rank", "settings", "history", "trials"].some((k) => k in raw)
  );
}
